'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { Currency } from '@/types/database.types'
import { CURRENCY_THEME } from '@/constants'

// ============================================
// TİPLER
// ============================================

type ThemeMode = 'light' | 'dark' | 'auto'

interface ThemeColors {
  primary: string
  secondary: string
  gradient: string
  background: string
  surface: string
  surfaceGlass: string
  text: string
  textMuted: string
  border: string
}

interface ThemeContextType {
  // Mod
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  isDark: boolean

  // Para birimi teması
  currency: Currency
  setCurrency: (currency: Currency) => void

  // Renkler
  colors: ThemeColors

  // CSS değişkenleri
  cssVars: Record<string, string>
}

// ============================================
// VARSAYILAN DEĞERLER
// ============================================

const darkModeColors: Omit<ThemeColors, 'primary' | 'secondary' | 'gradient'> = {
  background: '#0A0A0B',
  surface: '#141416',
  surfaceGlass: 'rgba(30, 30, 35, 0.8)',
  text: '#FAFAFA',
  textMuted: '#A1A1AA',
  border: '#27272A',
}

const lightModeColors: Omit<ThemeColors, 'primary' | 'secondary' | 'gradient'> = {
  background: '#FDFBF7',
  surface: '#FFFFFF',
  surfaceGlass: 'rgba(255, 255, 255, 0.7)',
  text: '#1A1A1A',
  textMuted: '#71717A',
  border: '#E8E2D6',
}

// ============================================
// CONTEXT
// ============================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// ============================================
// PROVIDER
// ============================================

interface ThemeProviderProps {
  children: ReactNode
  defaultMode?: ThemeMode
  defaultCurrency?: Currency
}

export function ThemeProvider({
  children,
  defaultMode = 'light',
  defaultCurrency = 'GRAM_GOLD',
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(defaultMode)
  const [currency, setCurrency] = useState<Currency>(defaultCurrency)
  const [systemDark, setSystemDark] = useState<boolean>(false)

  // Sistem teması algılama
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    setSystemDark(mediaQuery.matches)

    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mediaQuery.addEventListener('change', handler)

    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Local storage'dan tema okuma
  useEffect(() => {
    if (typeof window === 'undefined') return

    const savedMode = localStorage.getItem('gunum-var-theme') as ThemeMode | null
    const savedCurrency = localStorage.getItem('gunum-var-currency') as Currency | null

    if (savedMode) setModeState(savedMode)
    if (savedCurrency) setCurrency(savedCurrency)
  }, [])

  // Tema değiştiğinde kaydet
  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
    localStorage.setItem('gunum-var-theme', newMode)
  }

  // Para birimi değiştiğinde kaydet
  const handleSetCurrency = (newCurrency: Currency) => {
    setCurrency(newCurrency)
    localStorage.setItem('gunum-var-currency', newCurrency)
  }

  // Gerçek dark mode durumu
  const isDark = mode === 'dark' || (mode === 'auto' && systemDark)

  // Para birimine göre primary renkler
  const currencyColors = CURRENCY_THEME[currency]

  // Birleştirilmiş renkler
  const colors: ThemeColors = {
    primary: currencyColors.primary,
    secondary: currencyColors.secondary,
    gradient: currencyColors.gradient,
    ...(isDark ? darkModeColors : lightModeColors),
  }

  // CSS değişkenleri
  const cssVars: Record<string, string> = {
    '--color-primary': colors.primary,
    '--color-secondary': colors.secondary,
    '--color-gradient': colors.gradient,
    '--color-background': colors.background,
    '--color-surface': colors.surface,
    '--color-surface-glass': colors.surfaceGlass,
    '--color-text': colors.text,
    '--color-text-muted': colors.textMuted,
    '--color-border': colors.border,
  }

  // CSS değişkenlerini HTML'e uygula
  useEffect(() => {
    if (typeof document === 'undefined') return

    const root = document.documentElement
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })

    // Body sınıflarını güncelle
    root.classList.toggle('dark', isDark)
    root.classList.toggle('light', !isDark)
    root.setAttribute('data-currency', currency)
  }, [cssVars, isDark, currency])

  const value: ThemeContextType = {
    mode,
    setMode,
    isDark,
    currency,
    setCurrency: handleSetCurrency,
    colors,
    cssVars,
  }

  return (
    <ThemeContext.Provider value={value}>
      <div style={cssVars as React.CSSProperties}>{children}</div>
    </ThemeContext.Provider>
  )
}

// ============================================
// HOOK
// ============================================

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// ============================================
// YARDIMCI HOOK'LAR
// ============================================

/**
 * Sadece renkleri almak için kısa yol
 */
export function useColors() {
  const { colors } = useTheme()
  return colors
}

/**
 * Sadece dark mode kontrolü için kısa yol
 */
export function useIsDark() {
  const { isDark } = useTheme()
  return isDark
}

/**
 * Para birimi temasını almak için kısa yol
 */
export function useCurrencyTheme() {
  const { currency, colors } = useTheme()
  return {
    currency,
    primary: colors.primary,
    secondary: colors.secondary,
    gradient: colors.gradient,
  }
}
